import HyperDatepicker from "components/Datepicker";
import FancySelect from "components/FancySelect";
import React, { useEffect, useMemo, useState } from "react";
import { Card } from "react-bootstrap";
import Chart from "react-apexcharts";
import { format, startOfWeek } from "date-fns";
import { ApexOptions } from "apexcharts";
import axios from "axios";
import { getSelectColaboradores } from "helpers";
import { IColaborador } from "types";

interface IResultadoSemana {
  label: string;
  valor: number | null;
  total_respostas: number;
}

interface IData {
  resultados: IResultadoSemana[];
}

interface ISelectOption {
  value: number;
  label: string;
}

const PERMISSAO_RESPONDER_AUTOAVALIACAO = 159;

function DashboardPorColaborador() {
  const [data, setData] = useState<IData | null>(null);
  const [dataSelecionada, setDataSelecionada] = useState(new Date());
  const [quantidadeSemanas, setQuantidadeSemanas] = useState(8);
  const [colaboradores, setColaboradores] = useState<
    Array<{ id: number; apelido: string }>
  >([]);
  const [colaboradorSelecionado, setColaboradorSelecionado] =
    useState<ISelectOption | null>({
      value: 0,
      label: "Todos os colaboradores",
    });
  const [perguntas, setPerguntas] = useState<{ id: number; label: string }[]>(
    [],
  );
  const [perguntaSelecionada, setPerguntaSelecionada] =
    useState<ISelectOption | null>({
      value: 0,
      label: "Todas as perguntas",
    });
  const [semanasSelecionadas, setSemanasSelecionadas] = useState<ISelectOption>(
    {
      value: 8,
      label: "8 semanas",
    },
  );

  const inicio = useMemo(
    () => startOfWeek(dataSelecionada, { weekStartsOn: 5 }),
    [dataSelecionada],
  );

  useEffect(() => {
    const fetchPerguntas = async () => {
      const { data } = await axios.get(
        "/autoavaliacao-resposta/select-perguntas",
      );

      setPerguntas(
        (data as { id: number; label: string }[]).sort((a, b) =>
          a.label.localeCompare(b.label, "pt-BR"),
        ),
      );
    };

    fetchPerguntas();
  }, []);

  useEffect(() => {
    const fetchColaboradores = async () => {
      const [colaboradores] = await Promise.all([
        getSelectColaboradores("?autoavaliacao=true"),
      ]);

      setColaboradores(
        colaboradores.data.result.map((colaborador: IColaborador) => ({
          apelido: `${colaborador?.usuario?.nome} (${colaborador?.cargo?.nome})`,
          id: colaborador?.id,
        })),
      );
    };

    fetchColaboradores();
  }, []);

  useEffect(() => {
    setQuantidadeSemanas(semanasSelecionadas.value);
  }, [semanasSelecionadas]);

  useEffect(() => {
    const fetchData = async () => {
      const { data: resposta } = await axios.get(
        "/autoavaliacao-resposta/dashboard-respostas",
        {
          params: {
            ...(perguntaSelecionada && perguntaSelecionada.value !== 0
              ? { perguntafiltro_id: perguntaSelecionada.value }
              : {}),
            data_inicio: format(inicio, "yyyy-MM-dd"),
            quantidade_semanas: quantidadeSemanas,
            ...(colaboradorSelecionado && colaboradorSelecionado.value !== 0
              ? { colaborador_id: colaboradorSelecionado.value }
              : {}),
          },
        },
      );

      setData(resposta);
    };

    fetchData();
  }, [perguntaSelecionada, inicio, quantidadeSemanas, colaboradorSelecionado]);

  const categorias = useMemo(
    () => data?.resultados.map((item) => item.label) ?? [],
    [data],
  );

  const valores = useMemo(
    () => data?.resultados.map((item) => item.valor) ?? [],
    [data],
  );

  const cores = useMemo(
    () =>
      valores.map((valor) => {
        if (valor < 6) return "#dc3545";
        if (valor < 8) return "#ffc107";
        return "#28a745";
      }),
    [valores],
  );

  const valoresGrafico = valores.map((valor) => (valor === 0 ? 0.7 : valor));
  const series = useMemo(
    () => [
      {
        name: "Média geral",
        data: valoresGrafico.map((valor, index) => ({
          x: categorias[index] ?? `Semana ${index + 1}`,
          y: valor,
          fillColor: cores[index] ?? "#28a745",
          strokeColor: cores[index] ?? "#28a745",
        })),
      },
    ],
    [categorias, cores, valoresGrafico],
  );

  const options: ApexOptions = useMemo(
    () => ({
      chart: {
        type: "bar",
        toolbar: {
          show: false,
        },
      },
      plotOptions: {
        bar: {
          borderRadius: 4,
          columnWidth: "52%",
          distributed: true,
        },
      },
      colors: cores,
      dataLabels: {
        enabled: true,
        formatter: (_val, opts) => {
          const valor = valores[opts.dataPointIndex];
          return valor == null ? "" : Number(valor).toFixed(1);
        },
      },
      tooltip: {
        y: {
          formatter: (_val, opts) => {
            const valor = valores[opts.dataPointIndex];
            return valor == null ? "" : `${Number(valor).toFixed(2)} / 10`;
          },
        },
      },
      xaxis: {
        categories: categorias,
        labels: {
          rotate: -35,
          rotateAlways: true,
        },
        title: {
          text: "Período",
        },
      },
      yaxis: {
        min: 0,
        max: 10,
        title: {
          text: "Nota (0-10)",
        },
        labels: {
          formatter: (value) => Number(value).toFixed(1),
        },
      },
      legend: {
        show: false,
      },
    }),
    [categorias, cores, valores],
  );

  return (
    <>
      <Card>
        <Card.Body>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 16,
              flexWrap: "wrap",
              marginBottom: 16,
            }}
          >
            <h4 className="header-title mb-0">Respostas ao longo do tempo</h4>

            <div
              style={{
                display: "flex",
                gap: 12,
                alignItems: "center",
                flexWrap: "wrap",
              }}
            >
              {" "}
              <div style={{ width: 220 }}>
                <FancySelect
                  name="perguntas"
                  label=""
                  placeholder="Selecione"
                  isSearchable
                  value={
                    perguntaSelecionada && perguntaSelecionada.value !== 0
                      ? perguntaSelecionada
                      : { value: 0, label: "Todas as perguntas" }
                  }
                  options={[
                    { value: 0, label: "Todas as perguntas" },
                    ...perguntas.map((pergunta) => ({
                      value: pergunta.id,
                      label: pergunta.label,
                    })),
                  ]}
                  onChange={(selected: ISelectOption | null) => {
                    if (!selected || selected.value === 0) {
                      setPerguntaSelecionada({
                        value: 0,
                        label: "Todas as perguntas",
                      });
                      return;
                    }

                    setPerguntaSelecionada(selected);
                  }}
                />
              </div>
              <div style={{ width: 220 }}>
                <FancySelect
                  name="colaborador"
                  label=""
                  placeholder="Selecione"
                  isSearchable
                  value={
                    colaboradorSelecionado && colaboradorSelecionado.value !== 0
                      ? colaboradorSelecionado
                      : { value: 0, label: "Todos os colaboradores" }
                  }
                  options={[
                    { value: 0, label: "Todos os colaboradores" },
                    ...colaboradores.map((colaborador) => ({
                      value: colaborador.id,
                      label: colaborador.apelido,
                    })),
                  ]}
                  onChange={(selected: ISelectOption | null) => {
                    if (!selected || selected.value === 0) {
                      setColaboradorSelecionado({
                        value: 0,
                        label: "Todos os colaboradores",
                      });
                      return;
                    }

                    setColaboradorSelecionado(selected);
                  }}
                />
              </div>
              <div style={{ width: 140, marginTop: "3%" }}>
                <HyperDatepicker
                  hideAddon
                  value={inicio}
                  onChange={(date) => date && setDataSelecionada(date)}
                  dateFormat="dd/MM/yyyy"
                />
              </div>
              <div style={{ width: 200 }}>
                <FancySelect
                  name="semanas"
                  label=""
                  placeholder="Selecione"
                  value={semanasSelecionadas}
                  options={[4, 6, 8, 10, 12].map((qtd) => ({
                    value: qtd,
                    label: `${qtd} semanas`,
                  }))}
                  onChange={(selected: ISelectOption | null) => {
                    if (!selected) return;
                    setSemanasSelecionadas(selected);
                  }}
                />
              </div>
            </div>
          </div>

          <Chart
            options={options}
            series={series}
            type="bar"
            height={Math.max(400, categorias.length * 50)}
          />

          <div
            style={{
              marginTop: 20,
              display: "flex",
              justifyContent: "center",
              gap: 24,
              fontSize: 14,
              flexWrap: "wrap",
            }}
          >
            <div>
              <span
                style={{
                  display: "inline-block",
                  width: 12,
                  height: 12,
                  background: "#dc3545",
                  marginRight: 6,
                }}
              />
              Abaixo de 6
            </div>

            <div>
              <span
                style={{
                  display: "inline-block",
                  width: 12,
                  height: 12,
                  background: "#ffc107",
                  marginRight: 6,
                }}
              />
              Entre 6 e 8
            </div>

            <div>
              <span
                style={{
                  display: "inline-block",
                  width: 12,
                  height: 12,
                  background: "#28a745",
                  marginRight: 6,
                }}
              />
              Acima de 8
            </div>
          </div>
        </Card.Body>
      </Card>
    </>
  );
}

export default DashboardPorColaborador;
